import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import React, { useState } from 'react';
import { AxiosError } from 'axios';
import { useCommonContext } from '../../../hooks/useContexts';
import { ITenders } from '../../../models/common';
import { IPaymentMethods } from '../../../models/order-details';
import { returnCCImage, TCards } from '../../../utils/cc-images';
import { PaymentIconsAndDigits } from '../../shared/PaymentIconAndDigit';

const renderSkeletonLoader = () => {
  return (
    <SkeletonLine
      data-testid="tp-skeleton-loader"
      variant="single"
      numberOfLines={2}
      fullWidth
    />
  );
};

export const TablePayments = ({
  orderNumber,
  receiptDetails,
  tenders,
  paymentMethods,
  orderDetailsLoaded,
  orderDetailsError,
}: {
  orderNumber: string | undefined;
  receiptDetails?: string | undefined;
  tenders: ITenders[];
  paymentMethods?: IPaymentMethods[] | undefined;
  orderDetailsLoaded: boolean;
  orderDetailsError: AxiosError | null;
}) => {
  const { isTablet } = useCommonContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const modalSize = isTablet ? 'md' : 'sm';

  const orderPaymentMethods =
    (paymentMethods?.length ?? 0) > 0 ? paymentMethods : tenders;

  const renderCardTypeLogic = (type: TCards) => {
    if (type && returnCCImage(type, 50, 50)) {
      return <div className="cardImage">{returnCCImage(type, 50, 50)}</div>;
    }
    return null;
  };

  if (orderDetailsError) {
    return null;
  }

  if (!orderDetailsLoaded) {
    return renderSkeletonLoader();
  }

  const filterMatchingAmounts = (payments: (ITenders | IPaymentMethods)[]) => {
    const negativeAmounts = new Map();

    payments?.forEach((el) => {
      if ((el?.amount ?? 0) < 0) {
        const key = `${typeof el?.amount === 'number' ? el.amount * -1 : 0}-${
          'lastFour' in el ? el.lastFour : ''
        }`;
        negativeAmounts.set(key, true);
      }
    });

    return payments?.filter((el) => {
      const amount =
        typeof el.amount === 'number'
          ? el.amount
          : parseFloat(el.amount || '0');
      const lastFour = 'lastFour' in el ? el.lastFour : undefined;
      const key = `${amount}-${lastFour}`;
      if (amount > 0 && negativeAmounts.has(key)) {
        negativeAmounts.delete(key); // Remove to handle duplicates
        return false;
      }
      return true;
    });
  };

  const filteredOrderPaymentMethods = filterMatchingAmounts(
    orderPaymentMethods ?? [],
  );

  return (
    <>
      {(filteredOrderPaymentMethods?.length ?? 0) > 1 ? (
        <>
          <button
            type="button"
            className="sui-underline hover:sui-no-underline sui-text-ellipsis sui-text-base"
            onClick={() => setIsModalOpen(true)}
          >
            Multiple
          </button>
          <Modal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            size={modalSize}
          >
            <ModalHeader onClose={() => setIsModalOpen(false)}>
              {`Payments for ${orderNumber ? 'Order ' : 'Receipt '} # ${
                orderNumber ?? receiptDetails
              }`}
            </ModalHeader>
            <ModalBody>
              <div className="sui-pb-1">
                Below are the payment methods (
                <span style={{ fontWeight: 'bold' }}>
                  {filteredOrderPaymentMethods?.length}
                </span>
                ) used for this order:
              </div>
              <table className="sui-table sui-w-full ">
                <thead className="sui-table-header-group">
                  <tr className="sui-table-row sui-border-b-1 sui-border-solid sui-border-primary">
                    <th>Type</th>
                    <th>Account #</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrderPaymentMethods?.map((el, i: number) => {
                    return (
                      <tr key={i} className="sui-table-row">
                        <td align="center" className="sui-py-1">
                          {renderCardTypeLogic(
                            ((el as IPaymentMethods).cardType as TCards) ||
                              ((el as unknown as ITenders).type as TCards),
                          )}
                        </td>
                        {(el as IPaymentMethods)?.lastFour ||
                          (((el as unknown as ITenders)?.value as TCards) && (
                            <td align="center" className="sui-py-1">
                              <span>- </span>
                              {(el as IPaymentMethods)?.lastFour ||
                                ((el as unknown as ITenders)?.value as TCards)}
                            </td>
                          ))}
                        <td align="center" className="sui-py-1">
                          {el.amount
                            ? `$${Number(el.amount)?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ModalBody>
            <ModalFooter position="end">
              <Button variant="primary" onClick={() => setIsModalOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </>
      ) : (
        <PaymentIconsAndDigits
          payments={tenders}
          paymentMethods={paymentMethods}
        />
      )}
    </>
  );
};
